import { useState, useEffect } from 'react';
import { CommentsService, Comment } from '../services/comments.service';
import { authService } from '../services/auth.service';
import { toast } from 'react-toastify';

export const useComments = (id: string | undefined) => {
    const [comments, setComments] = useState<Comment[]>([]);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [showCommentDialog, setShowCommentDialog] = useState(false);
    const [commentText, setCommentText] = useState('');
    const [commentError, setCommentError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Fetch comments when component mounts
    useEffect(() => {
        const fetchComments = async () => {
            if (!id) return;

            setIsLoadingComments(true);
            try {
                const response = await CommentsService.getCommentsByJobId(id);
                setComments(response || []);
            } catch (error) {
                console.error('Error fetching comments:', error);
                toast.error('Failed to load comments');
            } finally {
                setIsLoadingComments(false);
            }
        };

        fetchComments();
    }, [id]);

    const handleCommentSubmit = async (fetchLatestAction: () => Promise<void>) => {
        if (!commentText.trim()) {
            setCommentError('Please enter a comment');
            return;
        }
        setIsSubmitting(true);
        setCommentError('');

        try {
            if (!id) {
                throw new Error('Jobsite ID not found');
            }

            const user = authService.getUser();
            if (!user?.id) {
                throw new Error('User information not found');
            }

            await CommentsService.createComment({
                comment: commentText,
                userId: user.id,
                jobId: id
            });

            // Fetch updated comments
            const response = await CommentsService.getCommentsByJobId(id);
            setComments(response || []);

            // Refresh latest action
            await fetchLatestAction();

            toast.success('Comment added successfully');
            setCommentText('');
            setShowCommentDialog(false);
        } catch (error) {
            console.error('Error submitting comment:', error);
            toast.error('Failed to submit comment. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        comments,
        setComments,
        isLoadingComments,
        showCommentDialog,
        setShowCommentDialog,
        commentText,
        setCommentText,
        commentError,
        setCommentError,
        isSubmitting,
        handleCommentSubmit
    };
}; 